import React, { useEffect, useRef, useState } from 'react';
import styles from './VideoWithPlayCountInput.module.scss';
import Text from '../../BaseModule/Components/Text';
import Video from '../../Base/Components/Video/Video';

interface Props {
	video: string;
	name: string;
	playDelayInSeconds?: number;
	maxPlays?: number;
	playRequired?: boolean;
	netteRules?: any;
	onVideoPlayed?: () => void;
	onVideoPlay?: () => void;
}

const ANIMATION_DURATION = 300;

const VideoWithPlayCountInput: React.FC<Props> = ({
	video,
	name,
	netteRules,
	playDelayInSeconds = 0,
	maxPlays = 999,
	playRequired = true,
	onVideoPlayed,
	onVideoPlay,
}) => {
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const playTimeoutRef = useRef<number | null>(null);
	const animationTimeoutRef = useRef<number | null>(null);

	const [playCount, setPlayCount] = useState(0);
	const [isVideoVisible, setIsVideoVisible] = useState(true);
	const [isVideoHiding, setIsVideoHiding] = useState(false);
	const [isVideoShowing, setIsVideoShowing] = useState(false);
	const [isWaitingToPlay, setIsWaitingToPlay] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const [shouldPlayWhenVisible, setShouldPlayWhenVisible] = useState(false);

	const canPlayAgain = playCount < maxPlays;

	const playVideo = () => {
		if (!canPlayAgain || isWaitingToPlay || isPlaying) {
			return;
		}
		if (animationTimeoutRef.current) {
			window.clearTimeout(animationTimeoutRef.current);
		}
		if (!isVideoVisible) {
			setIsVideoVisible(true);
			setIsVideoHiding(false);
			setIsVideoShowing(true);
			animationTimeoutRef.current = window.setTimeout(() => {
				setIsVideoShowing(false);
			}, ANIMATION_DURATION);
		}
		setShouldPlayWhenVisible(true);
		onVideoPlay?.();
	};

	useEffect(() => {
		if (!shouldPlayWhenVisible || !isVideoVisible || !videoRef.current) {
			return;
		}

		setIsWaitingToPlay(true);

		if (playTimeoutRef.current) {
			window.clearTimeout(playTimeoutRef.current);
		}

		playTimeoutRef.current = window.setTimeout(() => {
			videoRef.current?.play();
			setIsWaitingToPlay(false);
			setShouldPlayWhenVisible(false);
		}, playDelayInSeconds * 1000);

		return () => {
			if (playTimeoutRef.current) {
				window.clearTimeout(playTimeoutRef.current);
			}
		};
	}, [isVideoVisible, playDelayInSeconds, shouldPlayWhenVisible]);

	useEffect(() => {
		return () => {
			if (playTimeoutRef.current) {
				window.clearTimeout(playTimeoutRef.current);
			}

			if (animationTimeoutRef.current) {
				window.clearTimeout(animationTimeoutRef.current);
			}
		};
	}, []);

	const handleVideoPlay = () => {
		setIsPlaying(true);
	};

	const handleVideoPause = () => {
		setIsPlaying(false);
	};

	const handleVideoEnded = () => {
		setPlayCount(prev => prev + 1);
		setIsPlaying(false);
		setShouldPlayWhenVisible(false);
		setIsWaitingToPlay(false);
		setIsVideoHiding(true);

		if (animationTimeoutRef.current) {
			window.clearTimeout(animationTimeoutRef.current);
		}

		animationTimeoutRef.current = window.setTimeout(() => {
			setIsVideoVisible(false);
			setIsVideoHiding(false);
			setIsVideoShowing(false);

			if (videoRef.current) {
				videoRef.current.currentTime = 0;
			}
		}, ANIMATION_DURATION);
		onVideoPlayed?.();
	};

	const videoWrapperClassName = [
		styles.videoWrapper,
		isVideoHiding ? styles.videoWrapperHiding : '',
		isVideoShowing ? styles.videoWrapperShowing : '',
	]
		.filter(Boolean)
		.join(' ');

	//isPlaying = Video is playing
	//isWaitingToPlay = Play button already pressed, video is waiting for delay start if playDelayInSeconds is more than 0

	return (
		<div>
			{isVideoVisible && (
				<div className={videoWrapperClassName}>
					<Video
						videoSrc={video}
						videoRef={videoRef}
						className={styles.videoWrapperVideo}
						onPlay={handleVideoPlay}
						onPause={handleVideoPause}
						onEnded={handleVideoEnded}
					/>
					{!isPlaying && !isWaitingToPlay && playCount < 1 && (
						<div
							className={styles.videoWrapperPlayButton}
							onClick={playVideo}
						/>
					)}
				</div>
			)}

			{!isVideoVisible && canPlayAgain && (
				<div onClick={playVideo} className={styles.playAgainButton}>
					<div className={styles.playIcon} />
					<Text tx='component.videoWithPlayCountInput.playVideoAgain' />
				</div>
			)}

			<input
				data-nette-rules={netteRules}
				style={{ display: 'none' }}
				type='number'
				value={playCount}
				name={name}
				required={playRequired}
				readOnly
			/>
		</div>
	);
};

export default VideoWithPlayCountInput;
